import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { loginRequest } from 'B2C';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';
import { msalInstance } from 'index';
import { getTokenFromUrl } from 'Utils';
import { decodeJwtPayload } from 'Utils/decodeJwtPayload';

interface ErrorResponse {
  message: string;
}
export enum USER_TYPE {
  ShikumUser = '35',
  Representative = '54',
}

export const representativesAllowedUrls = [
  '/lawyers-lobby',
  '/documents',
  '/universalRequest',
  '/applies',
  '/recognitionStatus',
];

export const nonB2CUrls = ['/universalRequest', '/emergency-call/login'];

const timeoutManager = (() => {
  let requestsTimeOutId: number | null = null;
  return {
    set: (timeoutId: number) => {
      if (requestsTimeOutId) clearTimeout(requestsTimeOutId);
      requestsTimeOutId = timeoutId;
    },
    clear: () => {
      if (requestsTimeOutId) clearTimeout(requestsTimeOutId);
      requestsTimeOutId = null;
    },
  };
})();

export const api = axios.create({
  baseURL: import.meta.env.VITE_APP_DOMAIN,
  allowAbsoluteUrls: false,
});

function isLawyerOrShikumUser(decodedToken: any, userType: USER_TYPE): boolean {
  return (
    decodedToken?.userRanks?.findIndex(
      (r: string) =>
        r ===
        (userType === USER_TYPE.Representative
          ? USER_TYPE.Representative
          : USER_TYPE.ShikumUser)
    ) > -1
  );
}

const notAuthAction = async () => {
  if (window.location.pathname.includes('emergency-call')) {
    window.location.href = '/emergency-call/login';
    return;
  }
  if (
    !window.location.pathname
      .toLowerCase()
      .includes('universalRequest'.toLocaleLowerCase())
  )
    await msalInstance.loginRedirect(loginRequest);
};

const getToken = () =>
  sessionStorage.getItem('B2CToken') ||
  getTokenFromUrl() ||
  sessionStorage.getItem('token');

function checkRepresentativeRestrictions(token: string | null): boolean {
  if (!token) return false;
  const decodedToken = decodeJwtPayload(token);
  return (
    decodedToken &&
    isLawyerOrShikumUser(decodedToken, USER_TYPE.Representative) &&
    !isLawyerOrShikumUser(decodedToken, USER_TYPE.ShikumUser)
  );
}

async function handleAxiosError(
  error: AxiosError<ErrorResponse>,
  options: { isResponse?: boolean } = {}
) {
  const token = getToken();
  const status = error.response?.status;

  if (
    status === HTTP_STATUS_CODES.Unauthorized ||
    (options.isResponse && (error as any).correlationId)
  ) {
    await notAuthAction();
  } else if (status === HTTP_STATUS_CODES.Forbidden) {
    if (checkRepresentativeRestrictions(token)) {
      window.location.href = '/lawyers-lobby';
      if (options.isResponse) return Promise.reject(error);
    }
    timeoutManager.set(
      window.setTimeout(() => {
        alert(error.response?.data?.message || 'Forbidden access');
      }, 1000)
    );
  } else if (status === HTTP_STATUS_CODES.InternalServerError) {
    if (
      !(
        window.location.pathname.toLowerCase().includes('universalrequest') &&
        (error.response?.data as any)?.tryAgain === true
      )
    ) {
      window.location.href = '/error';
    }
  }
  return Promise.reject(error);
}

api.interceptors.request.use(
  async function (config: InternalAxiosRequestConfig) {
    const request = {
      scopes: loginRequest.scopes,
      account: msalInstance.getActiveAccount() || undefined,
    };
    let token: string | null;
    try {
      if (nonB2CUrls.some((url) => window.location.pathname.includes(url))) {
        token = getToken();
      } else {
        const response = await msalInstance.acquireTokenSilent(request);
        token = response.accessToken;
      }
    } catch (error) {
      token = getToken();
      console.error(error);
    }

    if (checkRepresentativeRestrictions(token)) {
      if (
        !representativesAllowedUrls.some((url) =>
          window.location.pathname.includes(url)
        )
      ) {
        window.location.href = '/lawyers-lobby';
      }
    }

    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;

    return config;
  },
  async function (error: AxiosError<ErrorResponse>) {
    return handleAxiosError(error);
  }
);

api.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error: AxiosError<ErrorResponse>) {
    console.error(error);
    return handleAxiosError(error, { isResponse: true });
  }
);
